import { useEffect, useId, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { formatUsPhoneDisplayFromAny } from "@/utils/phone";
import { useOrgModules } from "@/modules/useOrgModules";

type ChannelRow = {
  email?: string | null;
  number?: string | null;
  billing?: boolean;
};

const hasValue = (row: ChannelRow, key: "email" | "number") =>
  Boolean(String(row?.[key] ?? "").trim());

/**
 * "Use these details for billing": flags specific email/phone rows on the
 * contact. The flag lives on the row, so editing the address keeps it and
 * deleting the row removes it from billing.
 */
export const ContactBillingChannelsField = () => {
  // Billing contacts only matter with the Invoices app.
  const { isEnabled } = useOrgModules();
  if (!isEnabled("invoices")) return null;
  return <BillingChannelsToggle />;
};

const BillingChannelsToggle = () => {
  const id = useId();
  const { control, setValue } = useFormContext();
  const emails =
    (useWatch({ control, name: "email_jsonb" }) as ChannelRow[] | undefined) ??
    [];
  const phones =
    (useWatch({ control, name: "phone_jsonb" }) as ChannelRow[] | undefined) ??
    [];
  const anyFlagged = [...emails, ...phones].some((row) => row?.billing);
  const [enabled, setEnabled] = useState(anyFlagged);
  useEffect(() => {
    if (anyFlagged) setEnabled(true);
  }, [anyFlagged]);

  const setFlag = (
    source: "email_jsonb" | "phone_jsonb",
    index: number,
    value: boolean,
  ) =>
    setValue(`${source}.${index}.billing`, value, {
      shouldDirty: true,
    });

  const toggle = (checked: boolean) => {
    setEnabled(checked);
    if (checked) {
      if (anyFlagged) return;
      const email = emails.findIndex((row) => hasValue(row, "email"));
      const phone = phones.findIndex((row) => hasValue(row, "number"));
      if (email >= 0) setFlag("email_jsonb", email, true);
      if (phone >= 0) setFlag("phone_jsonb", phone, true);
      return;
    }
    emails.forEach((row, index) => {
      if (row?.billing) setFlag("email_jsonb", index, false);
    });
    phones.forEach((row, index) => {
      if (row?.billing) setFlag("phone_jsonb", index, false);
    });
  };

  const options = [
    ...emails.map((row, index) => ({
      key: `email-${index}`,
      label: String(row?.email ?? "").trim(),
      checked: Boolean(row?.billing),
      onChange: (value: boolean) => setFlag("email_jsonb", index, value),
      show: hasValue(row, "email"),
    })),
    ...phones.map((row, index) => ({
      key: `phone-${index}`,
      label: formatUsPhoneDisplayFromAny(String(row?.number ?? "")),
      checked: Boolean(row?.billing),
      onChange: (value: boolean) => setFlag("phone_jsonb", index, value),
      show: hasValue(row, "number"),
    })),
  ].filter((option) => option.show);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Switch
          id={`${id}-billing`}
          checked={enabled}
          onCheckedChange={toggle}
        />
        <Label htmlFor={`${id}-billing`} className="cursor-pointer">
          Use these details for billing
        </Label>
      </div>
      {enabled ? (
        options.length ? (
          <div className="space-y-1.5 pl-1">
            {options.map((option) => (
              <label
                key={option.key}
                className="flex items-center gap-2 text-sm"
              >
                <Checkbox
                  checked={option.checked}
                  onCheckedChange={(checked) =>
                    option.onChange(checked === true)
                  }
                />
                <span className="truncate">{option.label}</span>
              </label>
            ))}
            <p className="text-xs text-muted-foreground">
              Invoices, receipts and billing texts go to the checked details.
              With none checked, the primary email and phone are used.
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Add an email or phone above to choose billing details.
          </p>
        )
      ) : null}
    </div>
  );
};
